import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { INDIGO_HOVER } from '../../../colors';

interface Props {
  /*
   * text that is shown in the button that controls the open state.
   * */
  buttonText: string;
  /*
   * children.
   * */
  children: React.ReactNode;
  /*
   * decides whether the <Popper/> should close on children clicks.
   * */
  closeOnChildrenClick?: boolean;
  /*
   * controls the open state.
   * */
  forceOpen?: boolean;
  /*
   * defines if the ArrowDropDownIcon should render and whether the button text.
   * should be bold.
   * */
  selected?: boolean;
  icon?: string;
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
    },
  }),
);

export default function FilteringSelect(props: Props) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = (event) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  useEffect(() => {
    setOpen(props.forceOpen);
  }, [props.forceOpen]);

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      setOpen(false);
    }
  };

  return (
    <div className={classes.root}>
      <div
        onKeyDown={(event) => (event.key === 'Escape' ? setOpen(false) : null)}
        tabIndex={0}
      >
        <Button
          style={{
            textTransform: 'none',
            fontWeight: props.selected ? 600 : 'normal',
          }}
          sx={{
            color: props.selected ? INDIGO_HOVER : undefined,
            borderBottom: props.selected
              ? `2px solid ${INDIGO_HOVER}`
              : undefined,
            borderRadius: props.selected ? '4px 4px 0 0' : undefined,
            paddingBottom: props.selected ? '4px' : undefined,
          }}
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          {!props.icon ? (
            <>
              <div
                style={{
                  fontWeight: props.selected ? 600 : undefined,
                  color: props.selected ? INDIGO_HOVER : undefined,
                }}
              >
                {props.buttonText}
              </div>
              {!props.selected ? <ArrowDropDownIcon /> : null}
            </>
          ) : (
            <Icon icon={props.icon} color={'white'} fontSize={25} />
          )}
        </Button>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          disablePortal
          style={{ zIndex: 2000 }}
          placement={'bottom-start'}
        >
          <Paper>
            <ClickAwayListener onClickAway={handleClose}>
              <div
                onClick={
                  props.closeOnChildrenClick
                    ? () => {
                        setOpen(false);
                      }
                    : null
                }
                onKeyDown={handleKeyDown}
              >
                {props.children}
              </div>
            </ClickAwayListener>
          </Paper>
        </Popper>
      </div>
    </div>
  );
}

const defaultProps: Props = {
  buttonText: '',
  children: null,
  closeOnChildrenClick: true,
  forceOpen: false,
  selected: false,
};

FilteringSelect.defaultProps = defaultProps;
